var _excluded = ["disabled", "edges", "errorMessage", "fullWidth", "helperText", "helperTextPlacement", "hideLabel", "inputProps", "label", "name", "onBlur", "onChange", "onKeyDown", "required", "showError", "type", "placeholder", "value", "variant"];
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { AsYouType } from 'libphonenumber-js';
import InputAdornment from '@mui/material/InputAdornment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import FormControl from './FormControl';
import { StyledTextField } from './TextInput.styled';
var asYouType = new AsYouType('NZ');
var numberFormat = function numberFormat(value) {
  if (value) {
    var number = value.toString().replace(/[^.0-9]/g, '');
    if (number.indexOf('.') > 0) {
      number = number.split('.');
      number = number[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',') + "." + (number[1] ? number[1].substring(0, 2) : '');
    } else {
      number = number.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    return number;
  }
  return value;
};
export var phoneFormat = function phoneFormat(value) {
  asYouType.reset();
  asYouType.input(value);
  return asYouType.formattedOutput;
};
var TextInput = forwardRef(function (_ref, ref) {
  var disabled = _ref.disabled,
    edges = _ref.edges,
    errorMessage = _ref.errorMessage,
    fullWidth = _ref.fullWidth,
    helperText = _ref.helperText,
    helperTextPlacement = _ref.helperTextPlacement,
    hideLabel = _ref.hideLabel,
    inputProps = _ref.inputProps,
    label = _ref.label,
    name = _ref.name,
    onBlur = _ref.onBlur,
    onChange = _ref.onChange,
    onKeyDown = _ref.onKeyDown,
    required = _ref.required,
    showError = _ref.showError,
    type = _ref.type,
    placeholder = _ref.placeholder,
    value = _ref.value,
    variant = _ref.variant,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  var formatValue = function formatValue(v) {
    switch (type) {
      case 'currency':
        return numberFormat(v);
      case 'tel':
        return phoneFormat(v);
      default:
        return v;
    }
  };
  var parseAsNumber = function parseAsNumber(v) {
    if (v.charAt(v.length - 1) === '.' || !v) {
      return v;
    }
    return parseFloat(v.replace(/,/g, ''));
  };
  var handleChange = function handleChange(e) {
    if (type === 'currency') {
      return onChange(parseAsNumber(numberFormat(e.target.value)));
    }
    if (type === 'tel') {
      e.target.value = formatValue(e.target.value);
      return onChange(e);
    }
    return onChange(e);
  };
  var handleBlur = function handleBlur(e) {
    if (type === 'currency' && e.target.value.charAt(e.target.value.length - 1) === '.') {
      onChange(parseAsNumber(e.target.value + "00"));
    }
    onBlur(e);
  };
  var handleKeyDown = function handleKeyDown(e) {
    if (type === 'number' && isNaN(e.key) && e.key !== 'Backspace' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'ArrowUp' && e.key !== 'ArrowDown' && (e.key !== '.' || e.key === '.' && (e.target.value === '' || e.target.value.includes('.')))) {
      e.preventDefault();
    }
    onKeyDown(e);
  };
  var getIcon = function getIcon() {
    return type === 'currency' ? {
      startAdornment: /*#__PURE__*/React.createElement(InputAdornment, {
        position: "start"
      }, /*#__PURE__*/React.createElement(AttachMoneyIcon, null))
    } : null;
  };
  return /*#__PURE__*/React.createElement(FormControl, {
    fullWidth: fullWidth,
    name: name,
    label: label,
    helperText: helperText,
    helperTextPlacement: helperTextPlacement,
    disabled: disabled,
    required: required,
    showError: showError,
    errorMessage: errorMessage,
    edges: edges,
    hideLabel: hideLabel
  }, /*#__PURE__*/React.createElement(StyledTextField, _extends({
    ref: ref,
    name: name,
    label: null,
    margin: "normal",
    size: "small",
    variant: variant,
    required: required,
    error: showError,
    edges: edges,
    disabled: disabled,
    value: value ? formatValue(value) : '',
    type: type === 'currency' ? 'text' : type,
    inputProps: _extends({
      inputMode: type === 'number' || type === 'currency' ? 'decimal' : type,
      'aria-label': label,
      autoComplete: type === 'password' ? 'new-password' : 'off'
    }, inputProps),
    placeholder: placeholder
    // eslint-disable-next-line react/jsx-no-duplicate-props
    ,
    InputProps: getIcon(),
    onChange: handleChange,
    onBlur: handleBlur,
    onKeyDown: handleKeyDown
  }, rest)));
});
TextInput.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * If `true`, the `input` element will be disabled.
   */
  disabled: PropTypes.bool,
  /**
   * Make button edges rounded or square
   */
  edges: PropTypes.oneOf(['normal', 'rounded', 'sharp']),
  /**
   * The error message to display if showError === true
   */
  errorMessage: PropTypes.string,
  /**
   * If `true`, the input will take up the full width of its container.
   */
  fullWidth: PropTypes.bool,
  /**
   * The helper text content.
   */
  helperText: PropTypes.string,
  /**
   * Placed the helper text above or below the input.
   */
  helperTextPlacement: PropTypes.oneOf(['above', 'below']),
  /**
   * Make label visible only to screen readers
   */
  hideLabel: PropTypes.bool,
  /**
   * Attributes applied to the input element. When native is true, the attributes are applied on the select element
   */
  inputProps: PropTypes.object,
  /**
   * @ignore
   */
  onBlur: PropTypes.func,
  /**
   * @ignore
   */
  onChange: PropTypes.func,
  /**
   * @ignore
   */
  onKeyDown: PropTypes.func,
  /**
   * The label content.
   */
  label: PropTypes.string,
  /**
   * The field name.
   */
  name: PropTypes.string.isRequired,
  /**
   * The short hint displayed in the input before the user enters a value.
   */
  placeholder: PropTypes.string,
  /**
   * If `true`, the label is displayed as required and the `input` element` will be required.
   */
  required: PropTypes.bool,
  /**
   * Whether or not to display an error message (if there is a message to display).
   */
  showError: PropTypes.bool,
  /**
   * The input type attribute.
   */
  type: PropTypes.oneOf(['color', 'currency', 'email', 'number', 'password', 'search', 'tel', 'text', 'time', 'url']),
  /**
   * The value of the `input` element, required for a controlled component.
   */
  value: PropTypes.any,
  /**
   * The variant to use.
   */
  variant: PropTypes.oneOf(['filled', 'outlined'])
} : {};
TextInput.defaultProps = {
  disabled: false,
  edges: 'normal',
  errorMessage: '',
  fullWidth: false,
  helperText: '',
  helperTextPlacement: 'above',
  hideLabel: false,
  inputProps: {},
  label: '',
  onBlur: function onBlur() {
    return null;
  },
  onChange: function onChange() {
    return null;
  },
  onKeyDown: function onKeyDown() {
    return null;
  },
  placeholder: '',
  required: false,
  showError: false,
  type: 'text',
  value: undefined,
  variant: 'outlined'
};
export default TextInput;